<template>
  <!-- Backdrop click; Escape closes it natively -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <dialog ref="el" class="v5-dialog" :aria-labelledby="`${id}-h`" @close="emit('close')" @click.self="close">
    <div class="v5-dialog__box">
      <div class="v5-dialog__head">
        <span v-if="$slots.icon" class="v5-dialog__icon"><slot name="icon" /></span>
        <h2 :id="`${id}-h`">{{ title }}</h2>
        <button class="v5-ib" type="button" :aria-label="t('v5.shell.close')" @click="close"><XMarkIcon class="i" aria-hidden="true" /></button>
      </div>
      <slot />
    </div>
  </dialog>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { XMarkIcon } from '@heroicons/vue/24/outline';

// A native modal dialog: focus trap, Escape and the backdrop come with it. Mount it to open it;
// a click on the backdrop (the dialog itself, outside the box) closes it.
defineProps({
  id: { type: String, required: true },
  title: { type: String, required: true },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const el = ref(null);
// Focus goes back to whatever opened it, also when the parent unmounts it while open
let opener = null;
onMounted(() => {
  opener = document.activeElement;
  el.value.showModal?.();
});
onBeforeUnmount(() => {
  const a = document.activeElement;
  if (opener?.isConnected && (!a || a === document.body || el.value?.contains(a))) opener.focus?.();
});
// The native close fires @close; where there is none (jsdom), say so directly
const close = () => (el.value.close ? el.value.close() : emit('close'));
</script>

<style>
/* Form pieces the v5 dialogs share */
.v5-dialog .v5-field { display: grid; gap: 6px; color: var(--fg-2); font-size: var(--t-sm); font-weight: 500; }
.v5-dialog .v5-field small { color: var(--muted); font-size: var(--t-xs); font-weight: 400; }
.v5-dialog .v5-field input { height: 40px; padding: 0 10px; border: 1px solid var(--rule); border-radius: var(--r); background: var(--bg); color: var(--fg); font: inherit; }
.v5-dialog .v5-field input::placeholder { color: var(--muted); }
.v5-dialog .v5-field input[aria-invalid="true"] { border-color: var(--bad); }
.v5-dialog .v5-err { margin: 0; color: var(--bad); font-size: var(--t-sm); }
.v5-dialog .v5-dialog__text a { color: var(--fg); text-decoration: underline; text-underline-offset: 3px; }
.v5-dialog .v5-dialog__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.v5-dialog .v5-dialog__list { display: grid; max-height: 200px; margin: 0; padding: 0; overflow-y: auto; border: 1px solid var(--rule); border-radius: var(--r); list-style: none; }
.v5-dialog .v5-dialog__list li { padding: 8px 12px; overflow: hidden; color: var(--fg-2); font-size: var(--t-sm); text-overflow: ellipsis; white-space: nowrap; }
.v5-dialog .v5-dialog__list li + li { border-top: 1px solid var(--rule); }
@media (max-width: 44rem) { .v5-dialog .v5-field input { min-height: 44px; } .v5-dialog__actions > * { flex: 1; } }
</style>
